import React, { Suspense } from 'react'
import { createBrowserRouter, Navigate } from 'react-router-dom'
import OpsLayout from '@/layouts/OpsLayout'
import SignIn from '@/pages/auth/SignIn'
import AuthGuard from '@/app/core/auth/AuthGuard'
import RoleGuard from '@/app/core/auth/RoleGuard'
import { useAuth } from '@/app/core/auth/AuthProvider'
import { canAccessModule } from '@/app/core/auth/useRoles'

// React.lazy wrapper that survives deploys: when a hashed code-split chunk 404s because the tab is
// running an older build, reload once (guarded, shared with the vite:preloadError handler in
// main.tsx) to fetch the new build instead of crashing into the router error boundary.
function lazyWithReload<T extends React.ComponentType<any>>( // eslint-disable-line @typescript-eslint/no-explicit-any
  factory: () => Promise<{ default: T }>,
): React.LazyExoticComponent<T> {
  return React.lazy(() =>
    factory().catch((err) => {
      const KEY = 'chunk-reload-at'
      const now = Date.now()
      const last = Number(sessionStorage.getItem(KEY) ?? '0')
      if (now - last > 10_000) {
        sessionStorage.setItem(KEY, String(now))
        window.location.reload()
        // Never resolve — the reload is in flight, so don't surface the error meanwhile.
        return new Promise<{ default: T }>(() => {})
      }
      throw err
    }),
  )
}

const Dashboard = lazyWithReload(() => import('@/pages/inventory/Dashboard'))
const Products = lazyWithReload(() => import('@/pages/inventory/Products'))
const ProductDetail = lazyWithReload(() => import('@/pages/inventory/ProductDetail'))
const StockLevels = lazyWithReload(() => import('@/pages/inventory/StockLevels'))
const Batches = lazyWithReload(() => import('@/pages/inventory/Batches'))
const BatchDetail = lazyWithReload(() => import('@/pages/inventory/BatchDetail'))
const Movements = lazyWithReload(() => import('@/pages/inventory/Movements'))
const Receiving = lazyWithReload(() => import('@/pages/inventory/Receiving'))
const ReceivingNew = lazyWithReload(() => import('@/pages/inventory/ReceivingNew'))
const Transfers = lazyWithReload(() => import('@/pages/inventory/Transfers'))
const TransferNew = lazyWithReload(() => import('@/pages/inventory/TransferNew'))
const Dispatch = lazyWithReload(() => import('@/pages/inventory/Dispatch'))
const DispatchNew = lazyWithReload(() => import('@/pages/inventory/DispatchNew'))
const CycleCounts = lazyWithReload(() => import('@/pages/inventory/CycleCounts'))
const Reports = lazyWithReload(() => import('@/pages/inventory/Reports'))
const InventoryStatement = lazyWithReload(() => import('@/pages/inventory/InventoryStatement'))
const Settings = lazyWithReload(() => import('@/pages/inventory/Settings'))
const Profile = lazyWithReload(() => import('@/pages/inventory/Profile'))
const Help = lazyWithReload(() => import('@/pages/inventory/Help'))

const ProcDashboard = lazyWithReload(() => import('@/pages/procurement/Dashboard'))
const ProcOrders = lazyWithReload(() => import('@/pages/procurement/Orders'))
const ProcOrderNew = lazyWithReload(() => import('@/pages/procurement/OrderNew'))
const ProcOrderDetail = lazyWithReload(() => import('@/pages/procurement/OrderDetail'))
const ProcSuppliers = lazyWithReload(() => import('@/pages/procurement/Suppliers'))
const ProcSupplierDetail = lazyWithReload(() => import('@/pages/procurement/SupplierDetail'))
const ProcBills = lazyWithReload(() => import('@/pages/procurement/Bills'))
const ProcPayments = lazyWithReload(() => import('@/pages/procurement/Payments'))
const ProcReports = lazyWithReload(() => import('@/pages/procurement/Reports'))
const ProcSettings = lazyWithReload(() => import('@/pages/procurement/Settings'))
const ProcQcInspections = lazyWithReload(() => import('@/pages/procurement/QcInspections'))
const ProcQcInspectionDetail = lazyWithReload(() => import('@/pages/procurement/QcInspectionDetail'))
const ProcQcDeviations = lazyWithReload(() => import('@/pages/procurement/QcDeviations'))

const OrdersList = lazyWithReload(() => import('@/pages/orders/OrdersList'))
const OrderNew = lazyWithReload(() => import('@/pages/orders/OrderNew'))
const OrderDetail = lazyWithReload(() => import('@/pages/orders/OrderDetail'))

const SalDashboard = lazyWithReload(() => import('@/pages/sales/Dashboard'))
const SalContacts = lazyWithReload(() => import('@/pages/sales/Contacts'))
const SalContactDetail = lazyWithReload(() => import('@/pages/sales/ContactDetail'))
const SalDeals = lazyWithReload(() => import('@/pages/sales/Deals'))
const SalDealDetail = lazyWithReload(() => import('@/pages/sales/DealDetail'))
const SalPipeline = lazyWithReload(() => import('@/pages/sales/Pipeline'))
const SalQuotes = lazyWithReload(() => import('@/pages/sales/Quotes'))
const SalQuoteNew = lazyWithReload(() => import('@/pages/sales/QuoteNew'))
const SalQuoteDetail = lazyWithReload(() => import('@/pages/sales/QuoteDetail'))
const SalTasks = lazyWithReload(() => import('@/pages/sales/Tasks'))
const SalCalendar = lazyWithReload(() => import('@/pages/sales/Calendar'))
const SalOnboarding = lazyWithReload(() => import('@/pages/sales/Onboarding'))
const PublicOnboardingForm = lazyWithReload(() => import('@/pages/public/OnboardingForm'))
const SalReports = lazyWithReload(() => import('@/pages/sales/Reports'))

const AccDashboard = lazyWithReload(() => import('@/pages/accounting/Dashboard'))
const AccInvoices = lazyWithReload(() => import('@/pages/accounting/Invoices'))
const AccInvoiceNew = lazyWithReload(() => import('@/pages/accounting/InvoiceNew'))
const AccInvoiceDetail = lazyWithReload(() => import('@/pages/accounting/InvoiceDetail'))
const AccCustomers = lazyWithReload(() => import('@/pages/accounting/Customers'))
const AccCustomerDetail = lazyWithReload(() => import('@/pages/accounting/CustomerDetail'))
const AccJournal = lazyWithReload(() => import('@/pages/accounting/Journal'))
const AccJournalNew = lazyWithReload(() => import('@/pages/accounting/JournalNew'))
const AccJournalDetail = lazyWithReload(() => import('@/pages/accounting/JournalDetail'))
const AccAccounts = lazyWithReload(() => import('@/pages/accounting/Accounts'))
const AccNotes = lazyWithReload(() => import('@/pages/accounting/Notes'))
const AccReports = lazyWithReload(() => import('@/pages/accounting/Reports'))

const AdmDashboard = lazyWithReload(() => import('@/pages/admin/Dashboard'))
const AdmUsers = lazyWithReload(() => import('@/pages/admin/Users'))
const AdmInvitations = lazyWithReload(() => import('@/pages/admin/Invitations'))
const AdmOrganization = lazyWithReload(() => import('@/pages/admin/Organization'))
const AdmLegalEntityDetail = lazyWithReload(() => import('@/pages/admin/LegalEntityDetail'))
const AdmIntegrations = lazyWithReload(() => import('@/pages/admin/Integrations'))
const AdmMasterData = lazyWithReload(() => import('@/pages/admin/MasterData'))
const AdmAuditLogs = lazyWithReload(() => import('@/pages/admin/AuditLogs'))

function Lazy({
  Component,
}: {
  Component: React.LazyExoticComponent<React.ComponentType>
}) {
  return (
    <Suspense
      fallback={
        <div className="flex items-center justify-center py-20">
          <div className="text-sm text-muted-foreground">Loading…</div>
        </div>
      }
    >
      <Component />
    </Suspense>
  )
}

/** A user with no access to any built module sees this instead of a redirect loop. */
function NoAccess() {
  return (
    <div className="flex flex-col items-center justify-center text-center py-24 gap-2">
      <h2 className="text-lg font-bold text-foreground">No modules available</h2>
      <p className="text-sm text-muted-foreground max-w-sm">
        Your account doesn&apos;t have access to any available module yet. Contact an
        administrator to be assigned a role.
      </p>
    </div>
  )
}

/** Landing: route the user to the first built module their roles permit. */
function HomeRedirect() {
  const { user } = useAuth()
  const roles = user?.roles ?? []
  if (canAccessModule(roles, 'inventory')) return <Lazy Component={Dashboard} />
  if (canAccessModule(roles, 'procurement')) return <Navigate to="/procurement" replace />
  if (canAccessModule(roles, 'sales')) return <Navigate to="/sales" replace />
  if (canAccessModule(roles, 'orders')) return <Navigate to="/orders" replace />
  if (canAccessModule(roles, 'accounting')) return <Navigate to="/accounting" replace />
  if (canAccessModule(roles, 'admin')) return <Navigate to="/admin-portal" replace />
  return <NoAccess />
}

export const router = createBrowserRouter([
  { path: '/login', element: <SignIn /> },
  // Public partner onboarding form. Deliberately outside AuthGuard: applicants are doctors and
  // pharmacies who have no account. It posts to the anonymous /api/v1/public/onboarding intake.
  { path: '/apply', element: <Lazy Component={PublicOnboardingForm} /> },
  {
    element: <AuthGuard />,
    children: [
      {
        element: <OpsLayout />,
        children: [
          { index: true, element: <HomeRedirect /> },

          // Inventory module (role-gated)
          {
            element: <RoleGuard module="inventory" />,
            children: [
              { path: 'products', element: <Lazy Component={Products} /> },
              { path: 'products/:id', element: <Lazy Component={ProductDetail} /> },
              { path: 'stock', element: <Lazy Component={StockLevels} /> },
              { path: 'batches', element: <Lazy Component={Batches} /> },
              { path: 'batches/:id', element: <Lazy Component={BatchDetail} /> },
              { path: 'movements', element: <Lazy Component={Movements} /> },
              { path: 'receiving', element: <Lazy Component={Receiving} /> },
              { path: 'receiving/new', element: <Lazy Component={ReceivingNew} /> },
              { path: 'transfers', element: <Lazy Component={Transfers} /> },
              { path: 'transfers/new', element: <Lazy Component={TransferNew} /> },
              { path: 'dispatch', element: <Lazy Component={Dispatch} /> },
              { path: 'dispatch/new', element: <Lazy Component={DispatchNew} /> },
              { path: 'cycle-counts', element: <Lazy Component={CycleCounts} /> },
              { path: 'reports', element: <Lazy Component={Reports} /> },
              { path: 'statement', element: <Lazy Component={InventoryStatement} /> },
              // Read-only QC certificate view for warehouse/ops. Deliberately the SAME components as
              // the procurement route: they already render read-only because canWrite(roles,
              // 'procurement') is false for WAREHOUSE_USER/OPS_MANAGER, and the backend grants those
              // roles read access only. No duplicated page to drift out of sync.
              { path: 'qc', element: <Lazy Component={ProcQcInspections} /> },
              { path: 'qc/:id', element: <Lazy Component={ProcQcInspectionDetail} /> },
              { path: 'settings', element: <Lazy Component={Settings} /> },
            ],
          },

          // Procurement module (role-gated)
          {
            element: <RoleGuard module="procurement" />,
            children: [
              { path: 'procurement', element: <Lazy Component={ProcDashboard} /> },
              { path: 'procurement/orders', element: <Lazy Component={ProcOrders} /> },
              { path: 'procurement/orders/new', element: <Lazy Component={ProcOrderNew} /> },
              { path: 'procurement/orders/:id', element: <Lazy Component={ProcOrderDetail} /> },
              { path: 'procurement/suppliers', element: <Lazy Component={ProcSuppliers} /> },
              { path: 'procurement/suppliers/:id', element: <Lazy Component={ProcSupplierDetail} /> },
              { path: 'procurement/bills', element: <Lazy Component={ProcBills} /> },
              { path: 'procurement/payments', element: <Lazy Component={ProcPayments} /> },
              { path: 'procurement/qc', element: <Lazy Component={ProcQcInspections} /> },
              { path: 'procurement/qc/deviations', element: <Lazy Component={ProcQcDeviations} /> },
              { path: 'procurement/qc/:id', element: <Lazy Component={ProcQcInspectionDetail} /> },
              { path: 'procurement/reports', element: <Lazy Component={ProcReports} /> },
              { path: 'procurement/settings', element: <Lazy Component={ProcSettings} /> },
            ],
          },

          // Order Fulfilment module (role-gated)
          {
            element: <RoleGuard module="orders" />,
            children: [
              { path: 'orders', element: <Lazy Component={OrdersList} /> },
              { path: 'orders/new', element: <Lazy Component={OrderNew} /> },
              { path: 'orders/:id', element: <Lazy Component={OrderDetail} /> },
            ],
          },

          // Sales module (role-gated)
          {
            element: <RoleGuard module="sales" />,
            children: [
              { path: 'sales', element: <Lazy Component={SalDashboard} /> },
              { path: 'sales/contacts', element: <Lazy Component={SalContacts} /> },
              { path: 'sales/contacts/:id', element: <Lazy Component={SalContactDetail} /> },
              { path: 'sales/deals', element: <Lazy Component={SalDeals} /> },
              { path: 'sales/deals/:id', element: <Lazy Component={SalDealDetail} /> },
              { path: 'sales/pipeline', element: <Lazy Component={SalPipeline} /> },
              { path: 'sales/quotes', element: <Lazy Component={SalQuotes} /> },
              { path: 'sales/quotes/new', element: <Lazy Component={SalQuoteNew} /> },
              { path: 'sales/quotes/:id', element: <Lazy Component={SalQuoteDetail} /> },
              { path: 'sales/tasks', element: <Lazy Component={SalTasks} /> },
              { path: 'sales/calendar', element: <Lazy Component={SalCalendar} /> },
              { path: 'sales/onboarding', element: <Lazy Component={SalOnboarding} /> },
              { path: 'sales/reports', element: <Lazy Component={SalReports} /> },
            ],
          },

          // Accounting module (role-gated)
          {
            element: <RoleGuard module="accounting" />,
            children: [
              { path: 'accounting', element: <Lazy Component={AccDashboard} /> },
              { path: 'accounting/invoices', element: <Lazy Component={AccInvoices} /> },
              { path: 'accounting/invoices/new', element: <Lazy Component={AccInvoiceNew} /> },
              { path: 'accounting/invoices/:id', element: <Lazy Component={AccInvoiceDetail} /> },
              { path: 'accounting/customers', element: <Lazy Component={AccCustomers} /> },
              { path: 'accounting/customers/:id', element: <Lazy Component={AccCustomerDetail} /> },
              { path: 'accounting/journal', element: <Lazy Component={AccJournal} /> },
              { path: 'accounting/journal/new', element: <Lazy Component={AccJournalNew} /> },
              { path: 'accounting/journal/:id', element: <Lazy Component={AccJournalDetail} /> },
              { path: 'accounting/accounts', element: <Lazy Component={AccAccounts} /> },
              { path: 'accounting/notes', element: <Lazy Component={AccNotes} /> },
              { path: 'accounting/reports', element: <Lazy Component={AccReports} /> },
            ],
          },

          // Admin (Platform) module (role-gated: ADMIN full, AUDITOR read-only)
          {
            element: <RoleGuard module="admin" />,
            children: [
              { path: 'admin-portal', element: <Lazy Component={AdmDashboard} /> },
              { path: 'admin-portal/users', element: <Lazy Component={AdmUsers} /> },
              { path: 'admin-portal/invitations', element: <Lazy Component={AdmInvitations} /> },
              { path: 'admin-portal/organization', element: <Lazy Component={AdmOrganization} /> },
              { path: 'admin-portal/organization/:id', element: <Lazy Component={AdmLegalEntityDetail} /> },
              { path: 'admin-portal/integrations', element: <Lazy Component={AdmIntegrations} /> },
              { path: 'admin-portal/master-data', element: <Lazy Component={AdmMasterData} /> },
              { path: 'admin-portal/audit-logs', element: <Lazy Component={AdmAuditLogs} /> },
            ],
          },

          // Shared (any authenticated user)
          { path: 'profile', element: <Lazy Component={Profile} /> },
          { path: 'help', element: <Lazy Component={Help} /> },

          { path: '*', element: <Navigate to="/" replace /> },
        ],
      },
    ],
  },
])
